import { useNavigate, useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { SUPPORTED_LANGS, langMeta, langPrefix, stripLangPrefix } from '@/i18n';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const LanguageSwitcher = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { lang: currentLang } = useI18n();
  const { user } = useAuth();

  const switchLang = (newLang: typeof SUPPORTED_LANGS[number]) => {
    const basePath = stripLangPrefix(pathname);
    const newPath = `${langPrefix(newLang)}${basePath}`;
    localStorage.setItem('pt_geo_lang', newLang);
    localStorage.setItem('pt_lang', newLang);

    // Save preferred_lang to profiles if logged in
    if (user) {
      supabase
        .from('profiles')
        .update({ preferred_lang: newLang })
        .eq('user_id', user.id)
        .then(() => {});
    }

    navigate(newPath);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-sm" aria-label="Change language">
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{langMeta[currentLang].flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px] max-h-[70vh] overflow-y-auto">
        {SUPPORTED_LANGS.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onClick={() => switchLang(lang)}
            className={lang === currentLang ? 'bg-accent/10 font-semibold' : ''}
          >
            <span className="mr-2">{langMeta[lang].flag}</span>
            {langMeta[lang].name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
